// ============================================================================
// Trainer messaging hooks (Wave 5) — the TRAINER side of the 1:1 thread.
// ----------------------------------------------------------------------------
// Reads hang off qk.clients.* (the trainer realm) so the live-sync layer can
// invalidate them on a `message` change event (a client sending / reading
// broadcasts to the trainer hub). A trainer send/read invalidates the same keys
// so the open thread + the roster/tab unread badges update without a reload.
//
// `body` is plain UTF-8 user text + unicode emoji, rendered ESCAPED by the SPA
// (the shared <MessageThread> uses React's default escaping — never HTML).
// ============================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { messagesApi, qk } from "../api";
import type { MessageInput } from "../api";

/** Invalidate one client's thread + unread + the roster aggregate after a write. */
function invalidateThread(
  qc: ReturnType<typeof useQueryClient>,
  clientId: number,
) {
  void qc.invalidateQueries({ queryKey: qk.clients.messagesRoot(clientId) });
  void qc.invalidateQueries({ queryKey: qk.clients.messagesUnread(clientId) });
  void qc.invalidateQueries({ queryKey: qk.clients.unreadRoster });
}

/**
 * The trainer's roster-wide unread aggregate ({total, by_client}). One cheap
 * call feeds every unread badge — the per-row counts on the client roster and
 * the grand total — kept fresh by the `message` live-sync invalidation (no
 * polling). Enabled only when authenticated (the caller passes `enabled`).
 */
export function useRosterUnread(enabled = true) {
  return useQuery({
    queryKey: qk.clients.unreadRoster,
    queryFn: ({ signal }) => messagesApi.roster(signal),
    enabled,
    // The badge is purely additive; a brief staleness is fine and the WS
    // invalidation refetches on every real change anyway.
    staleTime: 30 * 1000,
  });
}

/** One owned client's thread (oldest→newest for display) + unread count. */
export function useClientThread(clientId: number | undefined, beforeId?: number) {
  return useQuery({
    queryKey: qk.clients.messages(clientId ?? -1, beforeId),
    queryFn: ({ signal }) =>
      messagesApi.list(clientId as number, { before_id: beforeId }, signal),
    enabled: clientId !== undefined && clientId >= 0,
  });
}

/** Post a text message into an owned client's thread (sender forced 'trainer'). */
export function useSendClientThreadMessage(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MessageInput) => messagesApi.send(clientId, body),
    onSuccess: () => invalidateThread(qc, clientId),
  });
}

/** Mark the thread read (stamps read_at on the CLIENT's unread messages). */
export function useMarkClientThreadRead(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => messagesApi.markRead(clientId),
    onSuccess: () => invalidateThread(qc, clientId),
  });
}

/** Send an image attachment (≤2 MB enforced server-side) + optional caption. */
export function useSendClientThreadAttachment(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, body }: { file: File; body?: string }) =>
      messagesApi.sendAttachment(clientId, file, body),
    onSuccess: () => invalidateThread(qc, clientId),
  });
}
